import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";

// Indirizzo evento dinamico: suggerimenti reali da /api/geocode (Nominatim/OSM via backend),
// debounce 400ms, minimo 3 caratteri, selezione da mouse/tocco/tastiera, nessun indirizzo inventato.
export function AddressAutocomplete({ value, onSelect }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value?.address || "");
  const [picked, setPicked] = useState(Boolean(value?.address));
  const [sugg, setSugg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const debRef = useRef(null);
  const rootRef = useRef(null);

  // Modifica del testo dopo una selezione -> selezione invalidata, serve nuova conferma
  const onChange = (v) => {
    setText(v);
    if (picked) { setPicked(false); onSelect(null); }
  };

  useEffect(() => {
    if (picked || text.trim().length < 3) { setSugg(null); setOpen(false); setLoading(false); return; }
    setLoading(true); setError(false);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      api.get("/geocode", { params: { q: text.trim() } })
        .then(({ data }) => { setSugg(data); setOpen(true); setHi(-1); })
        .catch(() => { setSugg(null); setError(true); setOpen(true); })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(debRef.current);
  }, [text, picked]);

  useEffect(() => {
    const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h, { passive: true });
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("touchstart", h); };
  }, []);

  const choose = (s) => {
    setText(s.display_name); setPicked(true); setOpen(false); setSugg(null); setHi(-1);
    onSelect({ address: s.display_name, city: s.city || "", region: s.region || "", country: s.country || "", lat: s.lat, lng: s.lng });
  };
  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); setHi(-1); return; }
    if (!open || !sugg?.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(i => (i + 1) % sugg.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(i => (i - 1 + sugg.length) % sugg.length); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); choose(sugg[hi]); }
  };

  return (
    <div className="relative" ref={rootRef}>
      <label className="text-sm font-medium" htmlFor="event-address-input">{t("events.address_label")}</label>
      <input id="event-address-input" role="combobox" aria-expanded={open} aria-controls="addr-listbox" aria-autocomplete="list"
        aria-activedescendant={hi >= 0 ? `addr-opt-${hi}` : undefined}
        className="yp-input mt-1" value={text} onChange={e => onChange(e.target.value)} onKeyDown={onKey}
        onFocus={() => { if (sugg?.length) setOpen(true); }}
        placeholder={t("events.address_ph")} required autoComplete="off" data-testid="event-address" />
      {loading && <div className="text-xs text-slate-400 mt-1" role="status" data-testid="address-loading">{t("addressx.loading")}</div>}
      {open && error && <div className="yp-addr-list yp-addr-msg yp-addr-error" role="alert" data-testid="address-error">{t("addressx.error")}</div>}
      {open && !error && sugg !== null && sugg.length === 0 && (
        <div className="yp-addr-list yp-addr-msg" role="status" data-testid="address-empty">{t("addressx.empty")}</div>)}
      {open && !error && sugg?.length > 0 && (
        <ul className="yp-addr-list" role="listbox" id="addr-listbox" aria-label={t("events.address_label")} data-testid="address-suggestions">
          {sugg.map((s, i) => (
            <li key={i} id={`addr-opt-${i}`} role="option" aria-selected={hi === i}
              className={`yp-addr-opt ${hi === i ? "yp-addr-opt-hi" : ""}`}
              onClick={() => choose(s)} onMouseEnter={() => setHi(i)} data-testid="address-suggestion">
              {s.display_name}
            </li>))}
        </ul>)}
      {picked && <div className="text-xs text-green-600 mt-1" role="status" data-testid="address-picked">✓ {t("addressx.picked")}</div>}
    </div>);
}
